import { Suspense, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { setUser, captureMessage, captureException } from "@sentry/react";
import to from "await-to-js";
import {
  Await,
  useLoaderData,
  useSubmit,
  ActionFunctionArgs,
  redirect,
  LoaderFunctionArgs,
  defer,
  useNavigate,
  useParams,
  useRevalidator,
} from "react-router-dom";
import {
  ApiError,
  AuthorizedService,
  checkoutResponse,
  getCheckoutQuery,
  getCheckoutQueryKey,
} from "matchi-api";
import { usePaymentLoadingStore } from "@/paymentLoadingStore";
import { queryClient } from "@/queryClient";
import { usePaymentServiceEvents } from "@/hooks";
import { track, identifyUser, trackPageView } from "@/analytics";
import { getTokenOrLogin } from "@/utils/auth";
import {
  initLocales,
  useTranslation,
  keycloakToI18nLocale,
  tGlobal,
} from "i18n";
import { resources } from "@/locales";
import {
  toast,
  OrderSummary,
  ApplyOffers,
  PaymentForm,
  ErrorBoundary,
  ReturnToAppButton,
} from "@/components";
import {
  PaymentServiceOption,
  InternalPaymentOption,
  MixedPaymentOption,
  getPaymentOptionsQueryKey,
  getPaymentServiceQuery,
  getPaymentOptionsQuery,
} from "@/queries";
import {
  poll,
  getQueryParam,
  isOrderCancelled,
  isOrderProcessed,
  isOrderCompleted,
  redirectWithReturnFallback,
  resolveSuccessTarget,
} from "@/utils";
import type { PaymentMethod } from "@adyen/adyen-web/dist/types/types";
import { ERROR_MESSAGES } from "@/utils/errors";

interface LoaderData {
  checkout: checkoutResponse;
  paymentOptions: MixedPaymentOption[];
  enableRecurring?: boolean;
  isRedirect?: boolean;
  redirectUrl?: string;
}

const REVALIDATE_EVENT = "REVALIDATE_EVENT";

/**
 * The loader found the order already completed, so the payment is
 * backend-confirmed and handing back to the app is safe here too.
 *
 * Its own component with its own effect, rather than a call in the render pass
 * that got us here: the redirect also arms a timer that writes to the payment
 * store a second later, and a render pass is the wrong place to start that.
 */
const RedirectToSuccess = ({ url }: { url: string }) => {
  const returnToAppUrl = usePaymentLoadingStore(state => state.returnToAppUrl);

  useEffect(() => {
    redirectWithReturnFallback(url);
  }, [url]);

  // This path renders no form, so a blocked handoff has no Pay CTA to take the
  // place of — give it one here.
  return returnToAppUrl ? <ReturnToAppButton url={returnToAppUrl} /> : null;
};

const Page = () => {
  const submit = useSubmit();
  const { data } = useLoaderData() as Awaited<ReturnType<typeof loader>>;
  const navigate = useNavigate();
  const { token } = useParams();
  const { t } = useTranslation();
  const [error, setError] = useState<string>();
  const { revalidate } = useRevalidator();

  useEffect(() => {
    const handler = () => {
      revalidate();
    };

    document.addEventListener(REVALIDATE_EVENT, handler);

    return () => {
      document.removeEventListener(REVALIDATE_EVENT, handler);
    };
  }, [revalidate]);

  const navigateToSuccess = () => {
    const { handBackToCaller, url } = resolveSuccessTarget(token!);

    // Only ever reached on a backend-confirmed terminal state — see the callers
    // below.
    if (handBackToCaller) return redirectWithReturnFallback(url);

    return navigate(url);
  };

  usePaymentServiceEvents({
    onPaymentCompleted: async e => {
      const { data } = e.detail;

      // v5: onPaymentCompleted fires for ALL result codes, including failures.
      // Handle error result codes first.
      // @see https://docs.adyen.com/online-payments/payment-result-codes/
      if (data.resultCode !== "Authorised") {
        usePaymentLoadingStore.setState({
          paymentFormSubmitting: false,
          verifyingRedirectResult: false,
        });

        captureMessage(`Adyen payment failed: ${data.resultCode}`, {
          level: data.resultCode === "Cancelled" ? "info" : "error",
          extra: { token, resultCode: data.resultCode, refusalReason: data.refusalReason },
        });

        switch (data.resultCode) {
          case "Refused":
            toast({
              title: t("Your payment was refused"),
              description: t(
                "To continue, please try again using another payment method.",
              ),
            });
            track("Payment Denied", {
              failure_type: "provider_refused",
              checkout_token: token,
              error_status: data.resultCode,
              error_code: data.refusalReason,
            });
            return;

          case "Cancelled":
            toast({
              title: t("Payment cancelled"),
              description: t("Please try again later."),
            });
            track("Payment Cancelled", {
              reason: "provider_cancelled",
              checkout_token: token,
            });
            return;

          default:
            toast({
              title: t("Failed to process payment"),
              description: t("Please try again later."),
            });
            track("Payment Denied", {
              failure_type: "provider_error",
              checkout_token: token,
              error_status: data.resultCode,
            });
            return;
        }
      }

      track("Payment Finished", {
        status: "success",
        provider_result_code: data.resultCode,
        checkout_token: token,
      });

      let [err, checkout] = await to(
        poll(
          () => AuthorizedService.getCheckout(token!),
          checkout => !isOrderProcessed(checkout),
          2000,
        ),
      );

      if (err || !checkout) {
        usePaymentLoadingStore.setState({
          paymentFormSubmitting: false,
          verifyingRedirectResult: false,
        });
        setError(t("Could not confirm order"));
        captureMessage("Could not confirm the order", {
          level: "error",
          extra: { token, error: err?.message },
        });
        track("Checkout Completed", {
          status: "error",
          reason: "confirmation_timeout",
          checkout_token: token,
        });
        return;
      }

      if (isOrderCancelled(checkout)) {
        setError(t("Could not confirm order"));
        usePaymentLoadingStore.setState({
          paymentFormSubmitting: false,
          verifyingRedirectResult: false,
        });
        track("Checkout Completed", {
          status: "error",
          reason: "order_cancelled",
          checkout_token: token,
        });
        return;
      }

      // MATCHi has confirmed the order and the payment is completed
      track("Checkout Completed", { status: "success", checkout_token: token });

      return navigateToSuccess();
    },

    onError: async e => {
      const { error } = e.detail;

      // User cancelling payments triggers this callback.
      // We dont want to show an error message in that case.
      if (error.name === "CANCEL") {
        usePaymentLoadingStore.setState({
          paymentFormSubmitting: false,
          verifyingRedirectResult: false,
        });
        track("Payment Cancelled", {
          reason: "user_cancelled",
          checkout_token: token,
        });
        return;
      }

      // Errors can be transient (common on mobile Safari when the browser
      // is backgrounded during 3DS). Check the backend first — the payment may
      // have already been processed via webhook.
      if (token) {
        const [err, checkout] = await to(AuthorizedService.getCheckout(token));

        if (!err && checkout && isOrderCompleted(checkout)) {
          track("Checkout Completed", {
            status: "success",
            reason: "recovered_after_error",
            checkout_token: token,
            error_code: error.name,
          });
          return navigateToSuccess();
        }
      }

      usePaymentLoadingStore.setState({
        paymentFormSubmitting: false,
        verifyingRedirectResult: false,
      });

      if (error.name === "NETWORK_ERROR") {
        toast({
          title: t("Connection lost"),
          description: t("Please check your connection and try again."),
        });
        captureMessage("Adyen NETWORK_ERROR", {
          level: "warning",
          extra: { token, message: error.message },
        });
      } else {
        toast({
          title: t("Failed to process payment"),
          description: t("Please try again later."),
        });
        captureException(error, {
          extra: { token, errorName: error.name },
        });
      }

      // Not a decline: this is the Adyen component failing client-side, which
      // includes the transient NETWORK_ERROR handled above. Tagged as such so a
      // decline-rate chart can exclude it.
      track("Payment Denied", {
        failure_type: "client_error",
        checkout_token: token,
        error_code: error.name,
        error_message: error.message,
      });
    },
  });

  return (
    <Suspense>
      <Await resolve={data}>
        {({
          isRedirect,
          redirectUrl,
          checkout,
          paymentOptions,
          enableRecurring,
        }: LoaderData) => {
          if (error) return <ErrorBoundary errorMessage={error} />;
          if (isRedirect) return <RedirectToSuccess url={redirectUrl!} />;

          return (
            <div className="flex flex-col gap-10">
              <OrderSummary checkout={checkout} />
              <ApplyOffers checkout={checkout} />
              <PaymentForm
                checkout={checkout}
                paymentOptions={paymentOptions}
                enableRecurring={enableRecurring}
                onSubmit={({ paymentOptionId }) => {
                  const formData = new FormData();
                  formData.append("paymentOptionId", paymentOptionId);
                  submit(formData, { method: "post" });
                }}
              />
            </div>
          );
        }}
      </Await>
    </Suspense>
  );
};

const loader = async ({ request, params: { token } }: LoaderFunctionArgs) => {
  // We need to return a promise from the loader or Suspense will not wait for the data to be fetched.
  // @see https://reactrouter.com/en/main/guides/deferred#using-defer
  const fetchCheckoutData = async () => {
    // No tracking here: this throw lands in the .catch below, which reports
    // Checkout Load Failed for every failure mode in one place.
    if (!token) throw new Error("No token found");

    usePaymentLoadingStore.setState({ isLoading: true });

    // Get bearer token from keycloak
    const bearer = await getTokenOrLogin();
    const { locale: userLocale, sub: keycloakId } =
      jwtDecode<KeycloakJWTPayload>(bearer);

    // Identify users in Amplitude..
    identifyUser({ keycloakId });

    // .. and Sentry
    setUser({ id: keycloakId });

    // Track the page view only once the user is identified — Amplitude does not
    // backfill the User ID onto events that were already sent.
    // request.url, not window.location: React Router runs loaders to completion
    // *before* completeNavigation pushes the history entry, so on a client-side
    // navigation window.location still holds the previous URL. This page is
    // usually a fresh document, where the two agree — but it is also reachable
    // by revalidation and by client-side navigation, and the success page next
    // door was silently broken by exactly this. Read the one that is always the
    // target.
    trackPageView(new URL(request.url).pathname);

    // Initialize i18next
    const locale = keycloakToI18nLocale(userLocale);
    await initLocales({ locale, namespace: "checkout", resources });

    let err: ApiError | null;
    let checkout, paymentService, paymentOptions;

    [err, checkout] = await to(
      queryClient.ensureQueryData(getCheckoutQuery(token)),
    );

    if (err?.body.code === "E2600") {
      throw new Error(ERROR_MESSAGES.INVALID_USER);
    }

    if (err?.body.code === "E2601") {
      throw new Error(ERROR_MESSAGES.MEMBERSHIP_INVOICED);
    }

    if (err || !checkout) throw new Error("Could not fetch checkout data");

    // If order is already processed, return the checkout data.
    // It will be handled in the then() callback
    if (isOrderProcessed(checkout)) return { checkout };

    [err, paymentService] = await to(
      queryClient.ensureQueryData(getPaymentServiceQuery(token)),
    );

    if (err || !paymentService)
      throw new Error("Could not initiate payment service");

    const { coupons } = checkout.paymentMethods;

    // Prepare payment options
    [err, paymentOptions] = await to(
      queryClient.ensureQueryData(
        getPaymentOptionsQuery(coupons, paymentService),
      ),
    );

    if (err || !paymentOptions) throw new Error("No payment methods found");

    if (checkout.adyenGiftCardOutcomes?.length) {
      // After a gift card partial payment, hide coupons and payment methods
      // that don't support Adyen's /orders (partial payment) flow.
      const UNSUPPORTED_WITH_ORDERS = ["swish"];
      const filtered = paymentOptions.filter(
        option =>
          option.type !== "COUPON" &&
          !(
            option.type === "PAYMENT_SERVICE" &&
            UNSUPPORTED_WITH_ORDERS.includes(
              (option as PaymentServiceOption).method.type,
            )
          ),
      );

      return {
        checkout,
        paymentOptions: filtered,
        paymentService,
        enableRecurring: paymentService.enableRecurring,
      };
    }

    return {
      checkout,
      paymentOptions,
      paymentService,
      enableRecurring: paymentService.enableRecurring,
    };
  };

  const data = fetchCheckoutData()
    .then(data => {
      // Handle orders that are completed
      if (isOrderCompleted(data.checkout)) {
        // fetchCheckoutData throws when the token is missing, so it is present
        // by the time this runs.
        const { url } = resolveSuccessTarget(token!);

        return { isRedirect: true, redirectUrl: url };
      }

      // Handle orders that are cancelled
      if (isOrderCancelled(data.checkout))
        throw new Error("Order was cancelled before completed payment");

      // Throw error if no payment methods are found
      if (!data.paymentService || !data.paymentOptions)
        throw new Error("No payment methods found");

      // Handle redirectResults
      // Some payment providers, like iDEAL, will redirect the user to a third party page to confirm the payment.
      // When the user is redirected back, the "redirectResult" query param will be added to the url.
      // We call the 'submitDetails' method and the rest is handled as any other payment in the 'onPaymentCompleted' callback.
      const redirectResult = getQueryParam("redirectResult");
      if (redirectResult) {
        usePaymentLoadingStore.setState({ verifyingRedirectResult: true });
        data.paymentService.submitDetails({ details: { redirectResult } });
      } else {
        usePaymentLoadingStore.setState({ isLoading: false });
      }

      return data;
    })
    .catch(error => {
      usePaymentLoadingStore.setState({
        isLoading: false,
        verifyingRedirectResult: false,
      });
      track("Checkout Load Failed", {
        source: "checkout",
        // This .catch wraps the whole chain, and not everything thrown out of
        // it is an Error — an Adyen object out of the synchronous submitDetails
        // above would send reason: undefined on a field the catalogue declares
        // required, and the event arrives unchartable.
        reason: String(error?.message ?? error),
        checkout_token: token,
      });
      throw error;
    });

  return defer({ data });
};

const action = async ({ request, params: { token } }: ActionFunctionArgs) => {
  usePaymentLoadingStore.setState({ paymentFormSubmitting: true });

  if (!token) {
    usePaymentLoadingStore.setState({ paymentFormSubmitting: false });
    track("Checkout Load Failed", {
      source: "checkout",
      reason: "No token found",
    });
    throw new Error("No token found");
  }

  const formData = await request.formData();
  const paymentOptionId = String(formData.get("paymentOptionId"));

  let err = null;

  // Handle free orders
  if (paymentOptionId === "FREE") {
    track("Payment Started", {
      success: true,
      source: "checkout",
      checkout_token: token,
      method: "FREE",
    });

    [err] = await to(
      AuthorizedService.createCheckoutBooking(token, {
        payment: { method: "FREE" },
      }),
    );

    if (err) {
      usePaymentLoadingStore.setState({ paymentFormSubmitting: false });
      toast({
        title: tGlobal("Could not confirm order"),
        description: tGlobal("Please try again later."),
      });
      track("Checkout Completed", {
        status: "error",
        reason: "free_order_failed",
        checkout_token: token,
        method: "FREE",
      });
      return null;
    }

    // Reset query to make sure user can't go back and submit the form again
    queryClient.resetQueries({ queryKey: getCheckoutQueryKey(token) });

    track("Payment Finished", {
      status: "success",
      checkout_token: token,
      method: "FREE",
    });
  } else {
    // Get selected payment option
    const paymentOption = queryClient
      .getQueryData<MixedPaymentOption[]>(getPaymentOptionsQueryKey)
      ?.find(({ id }) => id === paymentOptionId);

    if (!paymentOption) {
      usePaymentLoadingStore.setState({ paymentFormSubmitting: false });
      track("Payment Started", {
        success: false,
        source: "checkout",
        checkout_token: token,
        reason: "no_payment_option",
      });
      throw new Error("No payment methods found");
    }

    // Handle Adyen payment option
    if (paymentOption.type === "PAYMENT_SERVICE") {
      const { component, method } = paymentOption as PaymentServiceOption;

      // Google Pay's isValid is always false before user interaction.
      // @see https://github.com/Adyen/adyen-web/issues/2421
      if (method.type !== "googlepay" && !component.isValid) {
        usePaymentLoadingStore.setState({ paymentFormSubmitting: false });
        component.showValidation();
        track("Payment Started", {
          success: false,
          source: "checkout",
          checkout_token: token,
          method: method.type,
          reason: "invalid_form",
        });
        return null;
      }

      // If payment method is adyen gift card, hijack the flow to call balanceCheck.
      // @see https://docs.adyen.com/payment-methods/gift-cards/web-component/?tab=config-sessions_2
      if (method.type === "giftcard") {
        // Cache the previous gift card outcome to check against when polling in the callback
        let prevCheckout;
        let checkoutData;
        [err, prevCheckout] = await to(AuthorizedService.getCheckout(token));

        if (err || !prevCheckout) {
          usePaymentLoadingStore.setState({ paymentFormSubmitting: false });
          toast({
            title: tGlobal("Could not redeem gift card"),
            description: tGlobal(
              "Please try again later. If the problem persists, please contact support.",
            ),
          });
          // Reported against the submission, not against "Gift Card Applied":
          // this fires before balanceCheck(), so the balance check never
          // resolved and no order was re-priced.
          track("Gift Card Submitted", {
            success: false,
            checkout_token: token,
            reason: "precheck_failed",
          });
          return null;
        }

        const prev = prevCheckout?.adyenGiftCardOutcomes?.length ?? 0;

        // This callback will be triggered when the giftcard doesn't cover the full amount.
        component.props.onOrderCreated = async (_orderStatus: unknown) => {
          [err] = await to(
            poll(
              () => AuthorizedService.getCheckout(token),
              checkout => {
                // Poll until the "adyenGiftCardOutcomes" array is larger than it was before the giftcard was applied.
                // This means the giftcard has been applied and the checkout session should be up to date.
                const next = checkout.adyenGiftCardOutcomes?.length ?? 0;
                return next <= prev;
              },
              2000,
            ),
          );

          if (err) {
            toast({
              title: tGlobal("Could not redeem gift card"),
              description: tGlobal(
                "Please try again later. If the problem persists, please contact support.",
              ),
            });
            track("Gift Card Applied", {
              success: false,
              checkout_token: token,
              reason: "polling_failed",
            });
            usePaymentLoadingStore.setState({ paymentFormSubmitting: false });
            return null;
          }

          // Fetch the updated checkout data
          [err, checkoutData] = await to(AuthorizedService.getCheckout(token));

          if (err) {
            toast({
              title: tGlobal("Could not redeem gift card"),
              description: tGlobal(
                "Please try again later. If the problem persists, please contact support.",
              ),
            });
            track("Gift Card Applied", {
              success: false,
              checkout_token: token,
              reason: "refetch_failed",
            });
            usePaymentLoadingStore.setState({ paymentFormSubmitting: false });
            return null;
          }

          // Update the cache
          queryClient.setQueryData(getCheckoutQueryKey(token), checkoutData);

          // Get the payment service and..
          const paymentService = await queryClient.ensureQueryData(
            getPaymentServiceQuery(token),
          );

          // Clean up old payment components before recreating them to prevent
          // stale event listeners and duplicate event dispatch.
          const oldOptions = queryClient.getQueryData<MixedPaymentOption[]>(
            getPaymentOptionsQueryKey,
          );
          oldOptions?.forEach(opt => {
            if (opt.type === "PAYMENT_SERVICE") {
              (opt as PaymentServiceOption).component.remove();
            }
          });

          // Recreate the components with the updated paymentService.
          // At this point, the gift card has been applied and the components needs to be recreated in order for them to reflect the updated state
          await queryClient.fetchQuery(
            getPaymentOptionsQuery(
              checkoutData?.paymentMethods.coupons,
              paymentService,
            ),
          );

          track("Gift Card Applied", { success: true, checkout_token: token });

          // Unset the loading state and send event to revalidate the loader, making sure the DOM is rerendered.
          usePaymentLoadingStore.setState({ paymentFormSubmitting: false });
          const event = new CustomEvent(REVALIDATE_EVENT);
          document.dispatchEvent(event);

          return null;
        };

        // This callback will be triggered when the giftcard covers the full amount.
        component.props.onRequiringConfirmation = () => {
          component.submit();
        };

        track("Gift Card Submitted", {
          success: true,
          checkout_token: token,
          previous_gift_card_count: prev,
        });

        // @ts-expect-error - balanceCheck exists on v5 gift card component but is not in type definitions
        component.balanceCheck();
        return null;
      }

      track("Payment Started", {
        success: true,
        source: "checkout",
        checkout_token: token,
        method: method.type,
      });

      // Submit the payment form. The result is handled by onPaymentCompleted /
      // onError callbacks — NOT by this action's return value.
      // Return a never-resolving promise to prevent React Router from re-running
      // the loader, which would re-render and replace the card's DOM element while
      // the async 3DS flow still references it (causing "form is not connected").
      component.submit();
      return new Promise(() => {});
    }

    // Handle coupon (value card) payment option
    if (paymentOption.type === "COUPON") {
      const internalOption = paymentOption as InternalPaymentOption;

      track("Payment Started", {
        success: true,
        source: "checkout",
        checkout_token: token,
        method: internalOption.method.method,
      });

      [err] = await to(
        AuthorizedService.createCheckoutBooking(token, {
          payment: {
            method: internalOption.method.method!,
            id: internalOption.method.id,
          },
        }),
      );

      if (err) {
        usePaymentLoadingStore.setState({ paymentFormSubmitting: false });
        toast({
          title: tGlobal("Payment failed"),
          description: tGlobal("Please try again later."),
        });
        track("Checkout Completed", {
          status: "error",
          reason: "coupon_payment_failed",
          checkout_token: token,
          method: internalOption.method.method,
        });
        return null;
      }

      // Reset query to make sure user can't go back and submit the form again
      queryClient.resetQueries({ queryKey: getCheckoutQueryKey(token) });

      track("Payment Finished", {
        status: "success",
        checkout_token: token,
        method: internalOption.method.method,
      });
    }
  }

  // Wait for the order to be confirmed. `poll` settles on *either* terminal
  // state, so its result has to be inspected — see the cancelled check below.
  const [confirmErr, checkout] = await to(
    poll(
      () => AuthorizedService.getCheckout(token),
      checkout => !isOrderProcessed(checkout),
      2000,
    ),
  );

  if (confirmErr || !checkout) {
    usePaymentLoadingStore.setState({ paymentFormSubmitting: false });
    captureMessage("Could not confirm the order", {
      level: "error",
      extra: { token, error: confirmErr?.message },
    });
    track("Checkout Completed", {
      status: "error",
      reason: "confirmation_timeout",
      checkout_token: token,
    });
    throw new Error("Could not confirm the order.");
  }

  // `isOrderProcessed` is completed OR cancelled, so the poll above exits on a
  // cancelled order too. Handing `onSuccessUrl` over at that point would tell
  // the app an unpaid booking is paid — it treats arrival as proof of payment
  // and never re-checks. The Adyen path guards this in onPaymentCompleted; this
  // one needs its own.
  if (!isOrderCompleted(checkout)) {
    usePaymentLoadingStore.setState({ paymentFormSubmitting: false });
    captureMessage("Order was cancelled before completed payment", {
      level: "error",
      extra: { token, orderStatus: checkout.orderStatus },
    });
    track("Checkout Completed", {
      status: "error",
      reason: "order_cancelled",
      checkout_token: token,
    });
    throw new Error("Could not confirm the order.");
  }

  // Order is confirmed for free the free booking
  track("Checkout Completed", { status: "success", checkout_token: token });

  // Redirect the user to the success page or the onSuccessUrl
  const { handBackToCaller, url } = resolveSuccessTarget(token);

  if (handBackToCaller) {
    // Hand back to the app ourselves instead of via redirect(), so the fallback
    // timer is armed in the same code path as the navigation. The order is
    // confirmed and completed at this point — the poll and the check above
    // waited for it.
    //
    // Return a never-resolving promise like the Adyen path does: the document
    // is either leaving or the return button takes the Pay CTA's place, and
    // letting React Router finish the submission would only re-run the loader.
    redirectWithReturnFallback(url);
    return new Promise(() => {});
  }

  return redirect(url);
};

export const Payment = { Page, loader, action };
